import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { TagSchemaField } from '@/core/notifications/model';
import {
  fieldHeaderLabel,
  fieldLeafKeys,
  isFlatAllowedList,
} from '@/core/notifications/model/tag-schema';
import TagAllowedTree from '@/core/notifications/components/settings/subscriptions/tag-allowed-tree';
import FlatAllowedList from '@/core/notifications/components/settings/subscriptions/flat-allowed-list';

// One collapsible card per tagSchema field. The header (name, required badge,
// description) stays visible while collapsed so a long list of fields can be scanned
// without expanding each one; the value picker itself — and the invalid-value cleanup
// badges below it — only render once expanded, mirroring subscription-form.tsx's
// filter block but scoped to a single field instead of one global toggle.
export default function TagFieldCard({
  tagKey,
  field,
  selectedValues,
  onToggleLeaf,
  onToggleGroup,
  onFreeText,
  expanded,
  onToggleExpanded,
}: {
  tagKey: string;
  field: TagSchemaField;
  selectedValues: string[];
  onToggleLeaf: (leafKey: string) => void;
  onToggleGroup: (leafKeys: string[], checked: boolean) => void;
  onFreeText: (raw: string) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const hasAllowedList = !!field.allowed && field.allowed.length > 0;
  const leaves = hasAllowedList ? fieldLeafKeys(field) : [];
  // stored values that are no longer leaves (removed keys, or keys that became groups);
  // surfaced regardless of collapsed state so a bad stored value is never hidden from the user
  const invalidValues = hasAllowedList
    ? selectedValues.filter(value => !leaves.includes(value))
    : [];

  return (
    <div className="border rounded p-2 mb-2">
      <button
        type="button"
        className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none w-100"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
      >
        <FontAwesomeIcon
          icon={expanded ? faChevronDown : faChevronRight}
          size="xs"
          className="mutedText"
        />
        <span className="form-label mb-0" style={{ cursor: 'pointer' }} title={tagKey}>
          {fieldHeaderLabel(field, tagKey)}
        </span>
        {field.required && (
          <span
            className="badge fw-semibold"
            style={{
              background: 'rgba(var(--bs-danger-rgb), 0.2)',
              color: 'var(--bs-danger)',
              borderRadius: 2,
            }}
          >
            Required
          </span>
        )}
        {selectedValues.length > 0 && (
          <span className="badge bg-secondary">{selectedValues.length}</span>
        )}
      </button>

      {field.description && <div className="form-text">{field.description}</div>}

      {invalidValues.length > 0 && (
        <div className="mt-1">
          {invalidValues.map(value => (
            <span
              key={value}
              className="badge me-1 fw-semibold"
              style={{
                background: 'rgba(var(--bs-danger-rgb), 0.2)',
                color: 'var(--bs-danger)',
                borderRadius: 2,
              }}
            >
              {value}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1"
                style={{ color: 'var(--bs-danger)' }}
                onClick={() => onToggleLeaf(value)}
                aria-label={`Remove ${value}`}
              >
                ×
              </button>
            </span>
          ))}
          <div style={{ fontSize: 12, color: 'var(--bs-warning)' }}>
            These values are no longer available for this field. Remove them to save.
          </div>
        </div>
      )}

      {expanded && (
        <div className="mt-2">
          {hasAllowedList ? (
            isFlatAllowedList(field) ? (
              <FlatAllowedList
                entries={field.allowed ?? []}
                selected={selectedValues}
                onToggleLeaf={onToggleLeaf}
              />
            ) : (
              <TagAllowedTree
                entries={field.allowed ?? []}
                selected={selectedValues}
                onToggleLeaf={onToggleLeaf}
                onToggleGroup={onToggleGroup}
              />
            )
          ) : (
            <input
              className="form-control"
              placeholder="Comma-separate multiple values"
              value={selectedValues.join(', ')}
              onChange={event => onFreeText(event.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
}
